import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Link,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import MuiTooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { format, subMonths, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import {
  getMigrationGithubWorkflowMetrics,
  postMigrationGithubWorkflowMetricsSync,
  postMigrationGithubWorkflowMetricsSyncIncremental,
  postMigrationGithubWorkflowMetricsClear,
  postMigrationGithubWorkflowPathRefresh,
  postMigrationGithubWorkflowPathsRefresh
} from '../../services/apiService';
import {
  collectSourceDatacenters,
  collectTargetDatacenters,
  buildWeeklyFilteredClientTrend,
  buildWeeklyAllClientTrend,
  mergeWeeklyTrendChartData,
  filterMigrationsForTable
} from './migrationWorkflowChartUtils';

const AZURE_COLOR = '#1D4434';
/** Weekly chart: "All migrations" line - blue vs brand green filtered line. */
const ALL_MIGRATIONS_TREND_COLOR = '#1565C0';
const HOUR_PRESETS = [1, 2, 3, 4, 6, 8, 12, 24];

const SYNC_STATUS_HINTS = [
  'Listing workflow runs for the workflow and branch...',
  'Fetching each run and all re-run attempts from GitHub (this step is often the slowest)...',
  'Writing the new snapshot to the database...'
];

/** Quick sync uses fewer GitHub list pages so the request finishes before typical proxy timeouts. */
const GITHUB_SYNC_PAGES_RECENT = 3;
const GITHUB_SYNC_PAGES_FULL = 50;

const ATTEMPT_FILTER_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5+', label: '5+' }
];

export function formatDurationMs(ms) {
  if (ms == null || Number.isNaN(ms)) return '-';
  const n = Number(ms);
  if (n < 0) return '-';
  const sec = Math.floor(n / 1000);
  const m = Math.floor(sec / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h ${m % 60}m`;
  if (h > 0) return `${h}h ${m % 60}m ${sec % 60}s`;
  if (m > 0) return `${m}m ${sec % 60}s`;
  return `${sec}s`;
}

function formatDateTime(iso) {
  if (!iso) return '-';
  try {
    return format(new Date(iso), 'd MMM yyyy, HH:mm');
  } catch {
    return '-';
  }
}

function medianMs(values) {
  if (!values || values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Stable key for one migration path row (matches server groupKey identity). */
function migrationPathKey(row) {
  if (!row) return '';
  return `${row.clientName}|${row.sourceDatacenter}|${row.targetDatacenter}`;
}

/** Best-effort attempt start time (ms) for health window filtering. */
function attemptStartMsForHealth(a) {
  if (!a || typeof a !== 'object') return null;
  for (const iso of [a.startedAt, a.endedAt, a.runCreatedAt]) {
    if (!iso) continue;
    const t = new Date(iso).getTime();
    if (Number.isFinite(t)) return t;
  }
  return null;
}

/**
 * Keep only attempts whose start falls in [now − months, now]; drop paths with no such attempts.
 * Recomputes per-path attempt count, total duration, and latest* from in-window attempts only.
 */
/**
 * @param {number|null} fromMs  inclusive lower bound (null = no lower bound)
 * @param {number|null} toMs    inclusive upper bound (null = no upper bound)
 */
function buildMigrationsForHealthWindow(migrations, fromMs, toMs) {
  const out = [];
  for (const m of migrations || []) {
    const raw = Array.isArray(m.attempts) ? m.attempts : [];
    const attempts = raw
      .filter((a) => {
        const t = attemptStartMsForHealth(a);
        if (t == null) return false;
        if (fromMs != null && t < fromMs) return false;
        if (toMs != null && t > toMs) return false;
        return true;
      })
      .sort((a, b) => (attemptStartMsForHealth(b) ?? 0) - (attemptStartMsForHealth(a) ?? 0));
    if (attempts.length === 0) continue;
    const latest = attempts[0];
    const totalDurationMs = attempts.reduce((s, a) => s + (Number(a.durationMs) || 0), 0);

    out.push({
      ...m,
      attempts,
      attemptCount: attempts.length,
      totalDurationMs,
      latestStatus: latest?.status ?? null,
      latestConclusion: latest?.conclusion ?? null,
      latestRunCreatedAt: latest?.runCreatedAt ?? null,
      latestHtmlUrl: latest?.htmlUrl ?? null
    });
  }
  return out;
}

/**
 * Recharts LabelList `content` - clickable counts that open the same week modal as the line dots.
 * Note: Recharts `filterProps` strips `payload` from line points, so we resolve the row via `index` + `chartRows`.
 */
function WeeklyTrendCountLabel(props) {
  const {
    viewBox,
    position = 'top',
    offset = 5,
    value,
    index,
    chartRows,
    fill,
    fontSize,
    fontWeight,
    mode,
    onWeekClick,
    x,
    y
  } = props;

  if (value == null || Number(value) <= 0) return null;

  const row = Array.isArray(chartRows) && index != null && index >= 0 ? chartRows[index] : null;

  let labelX;
  let labelY;
  if (viewBox && typeof viewBox.x === 'number' && typeof viewBox.y === 'number') {
    const w = viewBox.width ?? 0;
    const h = viewBox.height ?? 0;
    const verticalSign = h >= 0 ? 1 : -1;
    if (position === 'top') {
      labelX = viewBox.x + w / 2;
      labelY = viewBox.y - verticalSign * offset;
    } else {
      labelX = viewBox.x + w / 2;
      labelY = viewBox.y + h / 2;
    }
  } else if (typeof x === 'number' && typeof y === 'number') {
    labelX = x;
    labelY = y - (offset ?? 5);
  } else {
    return null;
  }

  const handleActivate = (ev) => {
    ev.stopPropagation();
    if (row && onWeekClick) onWeekClick(row, mode);
  };

  return (
    <g style={{ pointerEvents: 'all' }} onClick={handleActivate}>
      <title>Click for client list</title>
      <rect
        x={labelX - 22}
        y={labelY - 14}
        width={44}
        height={22}
        fill="transparent"
        style={{ cursor: 'pointer' }}
      />
      <text
        x={labelX}
        y={labelY}
        fill={fill}
        fontSize={fontSize}
        fontWeight={fontWeight}
        textAnchor="middle"
        dominantBaseline="middle"
        pointerEvents="none"
      >
        {String(value)}
      </text>
    </g>
  );
}

/**
 * GitHub Actions workflow metrics (cut-over migration or instance provisioning).
 * @param {{ workflowProfile?: 'cutover' | 'provisioning' }} props
 */
const MigrationGitHubWorkflowsSection = ({ workflowProfile = 'cutover' }) => {
  const profileParam = workflowProfile === 'provisioning' ? 'provisioning' : 'cutover';
  const isProvisioning = profileParam === 'provisioning';
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [incrementalSyncLoading, setIncrementalSyncLoading] = useState(false);
  const [pathRefreshKey, setPathRefreshKey] = useState(null);
  const [filteredPathsRefreshLoading, setFilteredPathsRefreshLoading] = useState(false);
  const [pathsRefreshBanner, setPathsRefreshBanner] = useState(null);
  const [syncHintIndex, setSyncHintIndex] = useState(0);
  const [syncBanner, setSyncBanner] = useState(null);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [meta, setMeta] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);
  const [maxHoursFilter, setMaxHoursFilter] = useState(3);
  const [sourceDcFilter, setSourceDcFilter] = useState('');
  const [attemptCountFilter, setAttemptCountFilter] = useState('1');
  const [weekPointOpen, setWeekPointOpen] = useState(false);
  const [weekPointDetail, setWeekPointDetail] = useState(null);

  const [tableDateFrom, setTableDateFrom] = useState(null);
  const [tableDateTo, setTableDateTo] = useState(null);
  const [tableStatus, setTableStatus] = useState('all');
  const [tableSourceDc, setTableSourceDc] = useState('');
  const [tableTargetDc, setTableTargetDc] = useState('');
  const [tableClientSearch, setTableClientSearch] = useState('');
  const [tableMinDurH, setTableMinDurH] = useState('');
  const [tableMaxDurH, setTableMaxDurH] = useState('');
  const [tableMinAttempts, setTableMinAttempts] = useState('');
  const [tableMaxAttempts, setTableMaxAttempts] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [clearMongoDialogOpen, setClearMongoDialogOpen] = useState(false);
  const [clearMongoLoading, setClearMongoLoading] = useState(false);
  /** Migration health (snapshot) card: calendar window from today. Format: '7d', '15d', '30d', '3m', '6m', '12m'. */
  const [healthSnapshotWindowMonths, setHealthSnapshotWindowMonths] = useState('6m');
  /** 'relative' = rolling window, 'dateRange' = explicit from/to, 'priorTo' = before a date */
  const [healthPeriodMode, setHealthPeriodMode] = useState('relative');
  const [healthDateFrom, setHealthDateFrom] = useState(null);
  const [healthDateTo, setHealthDateTo] = useState(null);
  const pathTotalMs = (m) => Number(m.totalDurationMs) || 0;
  const [weekModalSortKey, setWeekModalSortKey] = useState('attemptCount');
  const [weekModalSortDir, setWeekModalSortDir] = useState('desc');
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(25);
  const [unparsedPage, setUnparsedPage] = useState(0);
  const [unparsedRowsPerPage, setUnparsedRowsPerPage] = useState(25);

  const load = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const res = await getMigrationGithubWorkflowMetrics({ profile: profileParam });
      if (res?.success) {
        startTransition(() => {
          setPayload(res.data ?? null);
          setMeta(res.meta || {});
        });
      } else {
        setPayload(null);
        setMeta({});
        setError(res?.message || 'Failed to load snapshot');
      }
    } catch (err) {
      console.error('GitHub workflow metrics:', err);
      setError(err.message || 'Failed to load workflow metrics');
      setPayload(null);
      setMeta({});
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [profileParam]);

  const handleRefreshPath = useCallback(async (row) => {
    if (!row) return;
    const k = migrationPathKey(row);
    try {
      setPathRefreshKey(k);
      setError(null);
      const res = await postMigrationGithubWorkflowPathRefresh({
        clientName: row.clientName,
        sourceDatacenter: row.sourceDatacenter,
        targetDatacenter: row.targetDatacenter,
        profile: profileParam
      });
      if (res?.success) {
        startTransition(() => {
          setPayload(res.data ?? null);
          setMeta(res.meta || {});
        });
        setDialogRow((prev) => {
          if (!prev || migrationPathKey(prev) !== k) return prev;
          const list = res.data?.migrations;
          if (!Array.isArray(list)) return prev;
          const updated = list.find((m) => migrationPathKey(m) === k);
          return updated || prev;
        });
      } else {
        setError(res?.message || 'Path refresh failed');
      }
    } catch (err) {
      console.error('Workflow path refresh:', err);
      setError(err.message || 'Path refresh failed');
    } finally {
      setPathRefreshKey(null);
    }
  }, [profileParam]);

  const handleFreshSync = useCallback(async (maxPages) => {
    try {
      setSyncLoading(true);
      setSyncBanner(null);
      setPathsRefreshBanner(null);
      setError(null);
      const res = await postMigrationGithubWorkflowMetricsSync(
        typeof maxPages === 'number' ? { maxPages, profile: profileParam } : { profile: profileParam }
      );
      if (res?.success) {
        const d = res.meta?.syncDiagnostics;
        if (d) {
          console.log('[MigrationGitHubWorkflows] sync diagnostics', d);
          if (profileParam !== 'provisioning') {
            const secs = d.totalMs != null ? (d.totalMs / 1000).toFixed(1) : '?';
            const cap = d.workflowRunsPageLimit;
            const capHint =
              cap != null && cap < GITHUB_SYNC_PAGES_FULL
                ? ` · GitHub run list capped at ${cap} page(s) (≤${cap * 100} runs)`
                : '';
            setSyncBanner({
              summary: `${d.runsListed ?? 0} workflow runs → ${d.migrationPaths ?? 0} migration paths in ${secs}s (server)${capHint}`,
              phases: Array.isArray(d.phases) ? d.phases : []
            });
          }
        }
        startTransition(() => {
          setPayload(res.data ?? null);
          setMeta(res.meta || {});
        });
        await load({ silent: true });
      } else {
        setError(res?.message || 'Sync failed');
      }
    } catch (err) {
      console.error('Workflow sync:', err);
      setError(err.message || 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  }, [load, profileParam]);

  const handleIncrementalSync = useCallback(async () => {
    try {
      setIncrementalSyncLoading(true);
      setSyncBanner(null);
      setPathsRefreshBanner(null);
      setError(null);
      const res = await postMigrationGithubWorkflowMetricsSyncIncremental({ profile: profileParam });
      if (res?.success) {
        const d = res.meta?.syncDiagnostics;
        if (d) {
          console.log('[MigrationGitHubWorkflows] incremental sync diagnostics', d);
          if (profileParam !== 'provisioning') {
            if (d.upToDate) {
              setSyncBanner({ summary: 'Already up to date - no new workflow runs found.', phases: [] });
            } else {
              const secs = d.totalMs != null ? (d.totalMs / 1000).toFixed(1) : '?';
              const cursorNote = d.reachedCursor ? '' : ' · did not reach cursor (more runs may exist)';
              setSyncBanner({
                summary: `${d.newRuns ?? 0} new workflow run(s) merged → ${d.migrationPaths ?? 0} migration paths in ${secs}s (server)${cursorNote}`,
                phases: Array.isArray(d.phases) ? d.phases : []
              });
            }
          }
        }
        startTransition(() => {
          setPayload(res.data ?? null);
          setMeta(res.meta || {});
        });
        await load({ silent: true });
      } else {
        setError(res?.message || 'Incremental sync failed');
      }
    } catch (err) {
      console.error('Incremental workflow sync:', err);
      setError(err.message || 'Incremental sync failed');
    } finally {
      setIncrementalSyncLoading(false);
    }
  }, [load, profileParam]);

  const handleConfirmClearMongo = useCallback(async () => {
    try {
      setClearMongoLoading(true);
      setError(null);
      const res = await postMigrationGithubWorkflowMetricsClear({ profile: profileParam });
      if (res?.success) {
        setClearMongoDialogOpen(false);
        setSyncBanner(null);
        setPathsRefreshBanner(null);
        startTransition(() => {
          setPayload(null);
          setMeta({});
        });
        await load({ silent: true });
      } else {
        setError(res?.message || 'Failed to clear stored statistics');
      }
    } catch (err) {
      console.error('Clear migration workflow snapshot:', err);
      setError(err.message || 'Failed to clear stored statistics');
    } finally {
      setClearMongoLoading(false);
    }
  }, [load, profileParam]);

  useEffect(() => {
    if (!syncLoading) {
      return undefined;
    }
    setSyncHintIndex(0);
    const id = setInterval(() => {
      setSyncHintIndex((i) => (i + 1) % SYNC_STATUS_HINTS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [syncLoading]);

  useEffect(() => {
    load();
  }, [load]);

  const sourceDcOptions = useMemo(() => collectSourceDatacenters(payload?.migrations), [payload]);
  const targetDcOptions = useMemo(() => collectTargetDatacenters(payload?.migrations), [payload]);

  const tableFilterParams = useMemo(
    () => ({
      dateFromMs: tableDateFrom ? tableDateFrom.startOf('day').valueOf() : null,
      dateToMs: tableDateTo ? tableDateTo.endOf('day').valueOf() : null,
      status: tableStatus,
      sourceDc: tableSourceDc,
      targetDc: tableTargetDc,
      clientSearch: tableClientSearch,
      minDurationHours: tableMinDurH,
      maxDurationHours: tableMaxDurH,
      minAttempts: tableMinAttempts,
      maxAttempts: tableMaxAttempts
    }),
    [
      tableDateFrom,
      tableDateTo,
      tableStatus,
      tableSourceDc,
      tableTargetDc,
      tableClientSearch,
      tableMinDurH,
      tableMaxDurH,
      tableMinAttempts,
      tableMaxAttempts
    ]
  );

  const filteredMigrations = useMemo(
    () => filterMigrationsForTable(payload?.migrations, tableFilterParams),
    [payload?.migrations, tableFilterParams]
  );

  const handleRefreshFilteredPaths = useCallback(async () => {
    const rows = filteredMigrations;
    if (!rows || rows.length === 0) return;
    if (rows.length > 200) {
      setError('At most 200 paths can be refreshed at once. Narrow your table filters.');
      return;
    }
    try {
      setFilteredPathsRefreshLoading(true);
      setPathsRefreshBanner(null);
      setError(null);
      const paths = rows.map((row) => ({
        clientName: row.clientName,
        sourceDatacenter: row.sourceDatacenter,
        targetDatacenter: row.targetDatacenter
      }));
      const res = await postMigrationGithubWorkflowPathsRefresh({ paths, profile: profileParam });
      if (res?.success) {
        startTransition(() => {
          setPayload(res.data ?? null);
          setMeta(res.meta || {});
        });
        const d = res.meta?.syncDiagnostics;
        if (d?.partial && d.pathsNotFound > 0) {
          setPathsRefreshBanner({
            severity: 'warning',
            message: `Refreshed ${d.pathsRefreshed} of ${d.pathsRequested} paths. ${d.pathsNotFound} had no matching workflow runs in the current GitHub list.`
          });
        } else {
          const secs = d?.totalMs != null ? (d.totalMs / 1000).toFixed(1) : '?';
          setPathsRefreshBanner({
            severity: 'success',
            message: `Refreshed ${d?.pathsRefreshed ?? rows.length} path(s) from GitHub (${secs}s, one list fetch).`
          });
        }
        setDialogRow((prev) => {
          if (!prev) return prev;
          const k = migrationPathKey(prev);
          const list = res.data?.migrations;
          if (!Array.isArray(list)) return prev;
          const updated = list.find((m) => migrationPathKey(m) === k);
          return updated || prev;
        });
      } else {
        setError(res?.message || 'Batch refresh failed');
      }
    } catch (err) {
      console.error('Workflow paths refresh:', err);
      setError(err.message || 'Batch refresh failed');
    } finally {
      setFilteredPathsRefreshLoading(false);
    }
  }, [filteredMigrations, profileParam]);

  const paginatedMigrations = useMemo(() => {
    const start = tablePage * tableRowsPerPage;
    return filteredMigrations.slice(start, start + tableRowsPerPage);
  }, [filteredMigrations, tablePage, tableRowsPerPage]);

  useEffect(() => {
    setTablePage(0);
  }, [tableFilterParams]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredMigrations.length / tableRowsPerPage) - 1);
    if (tablePage > maxPage) setTablePage(maxPage);
  }, [filteredMigrations.length, tableRowsPerPage, tablePage]);

  const unparsedRunsList = useMemo(() => payload?.unparsedRuns ?? [], [payload?.unparsedRuns]);
  const paginatedUnparsedRuns = useMemo(() => {
    const start = unparsedPage * unparsedRowsPerPage;
    return unparsedRunsList.slice(start, start + unparsedRowsPerPage);
  }, [unparsedRunsList, unparsedPage, unparsedRowsPerPage]);

  useEffect(() => {
    const n = unparsedRunsList.length;
    const maxPage = Math.max(0, Math.ceil(n / unparsedRowsPerPage) - 1);
    if (unparsedPage > maxPage) setUnparsedPage(maxPage);
  }, [unparsedRunsList.length, unparsedRowsPerPage, unparsedPage]);

  const clearTableFilters = useCallback(() => {
    setTableDateFrom(null);
    setTableDateTo(null);
    setTableStatus('all');
    setTableSourceDc('');
    setTableTargetDc('');
    setTableClientSearch('');
    setTableMinDurH('');
    setTableMaxDurH('');
    setTableMinAttempts('');
    setTableMaxAttempts('');
  }, []);

  const periodRange = useMemo(() => {
    let fromMs = null;
    let toMs = null;
    let windowLabel = '';
    let cutoffLabel = '-';
    if (healthPeriodMode === 'dateRange') {
      fromMs = healthDateFrom ? healthDateFrom.startOf('day').valueOf() : null;
      toMs = healthDateTo ? healthDateTo.endOf('day').valueOf() : null;
      const fromStr = fromMs ? format(new Date(fromMs), 'd MMM yyyy') : null;
      const toStr = toMs ? format(new Date(toMs), 'd MMM yyyy') : null;
      if (fromStr && toStr) windowLabel = `${fromStr} – ${toStr}`;
      else if (fromStr) windowLabel = `from ${fromStr} onwards`;
      else if (toStr) windowLabel = `up to ${toStr}`;
      else windowLabel = 'all time';
      cutoffLabel = fromStr ?? '(no lower bound)';
    } else if (healthPeriodMode === 'priorTo') {
      toMs = healthDateTo ? healthDateTo.endOf('day').valueOf() : null;
      const toStr = toMs ? format(new Date(toMs), 'd MMM yyyy') : null;
      windowLabel = toStr ? `prior to ${toStr}` : 'all time';
      cutoffLabel = toStr ?? '(all time)';
    } else {
      const periodStr = String(healthSnapshotWindowMonths || '6m');
      let cutoffDate;
      if (periodStr.endsWith('d')) {
        const days = Math.max(1, parseInt(periodStr, 10) || 7);
        cutoffDate = subDays(new Date(), days);
        windowLabel = `${days}-day`;
      } else {
        const months = Math.min(120, Math.max(1, Number(periodStr.replace('m', '')) || 6));
        cutoffDate = subMonths(new Date(), months);
        windowLabel = `${months}-month`;
      }
      fromMs = cutoffDate.getTime();
      cutoffLabel = format(cutoffDate, 'd MMM yyyy');
    }
    return { fromMs, toMs, windowLabel, cutoffLabel };
  }, [healthPeriodMode, healthSnapshotWindowMonths, healthDateFrom, healthDateTo]);

  const weeklyClientTrend = useMemo(
    () =>
      buildWeeklyFilteredClientTrend(
        payload?.migrations,
        maxHoursFilter,
        sourceDcFilter || '',
        attemptCountFilter,
        periodRange.fromMs,
        periodRange.toMs
      ),
    [payload, maxHoursFilter, sourceDcFilter, attemptCountFilter, periodRange]
  );

  const weeklyAllClientTrend = useMemo(
    () => buildWeeklyAllClientTrend(payload?.migrations, periodRange.fromMs, periodRange.toMs),
    [payload?.migrations, periodRange]
  );

  const weeklyChartData = useMemo(
    () => mergeWeeklyTrendChartData(weeklyAllClientTrend, weeklyClientTrend),
    [weeklyAllClientTrend, weeklyClientTrend]
  );

  const handleWeekPointClick = useCallback((row, mode) => {
    if (!row || !row.week) return;
    const isAll = mode === 'all';
    setWeekPointDetail({
      week: row.week,
      clientCount: isAll ? row.allCount : row.filteredCount,
      items: isAll ? row.allItems : row.filteredItems,
      mode: isAll ? 'all' : 'filtered'
    });
    setWeekModalSortKey('attemptCount');
    setWeekModalSortDir('desc');
    setWeekPointOpen(true);
  }, []);

  const sortedWeekItems = useMemo(() => {
    if (!weekPointDetail?.items) return [];
    const items = [...weekPointDetail.items];
    const dir = weekModalSortDir === 'asc' ? 1 : -1;
    items.sort((a, b) => {
      switch (weekModalSortKey) {
        case 'attemptCount': return dir * ((a.attemptCount ?? 0) - (b.attemptCount ?? 0));
        case 'totalDurationMs': return dir * ((a.totalDurationMs ?? 0) - (b.totalDurationMs ?? 0));
        case 'clientName': return dir * String(a.clientName ?? '').localeCompare(String(b.clientName ?? ''));
        case 'sourceDatacenter': return dir * String(a.sourceDatacenter ?? '').localeCompare(String(b.sourceDatacenter ?? ''));
        default: return 0;
      }
    });
    return items;
  }, [weekPointDetail?.items, weekModalSortKey, weekModalSortDir]);

  const handleWeekModalSort = useCallback((col) => {
    if (weekModalSortKey === col) {
      setWeekModalSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setWeekModalSortKey(col);
      setWeekModalSortDir('desc');
    }
  }, [weekModalSortKey]);

  const exportWeekItemsCsv = useCallback(() => {
    if (!sortedWeekItems.length) return;
    const headers = ['Client', 'Source DC', 'Target DC', 'Latest Status', 'Attempts', 'Total Duration (ms)', 'GitHub URL'];
    const rows = sortedWeekItems.map((r) => [
      r.clientName ?? '',
      r.sourceDatacenter ?? '',
      r.targetDatacenter ?? '',
      r.latestStatus === 'completed' ? (r.latestConclusion ?? '') : (r.latestStatus ?? ''),
      r.attemptCount ?? '',
      r.totalDurationMs ?? '',
      r.latestHtmlUrl ?? ''
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `week-${weekPointDetail?.week ?? 'export'}-migrations.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [sortedWeekItems, weekPointDetail?.week]);

  const filteredCountLabelContent = useCallback(
    (labelProps) => (
      <WeeklyTrendCountLabel
        viewBox={labelProps.viewBox}
        position={labelProps.position}
        offset={labelProps.offset}
        value={labelProps.value}
        index={labelProps.index}
        x={labelProps.x}
        y={labelProps.y}
        fill={AZURE_COLOR}
        fontSize={11}
        fontWeight={600}
        mode="filtered"
        chartRows={weeklyChartData}
        onWeekClick={handleWeekPointClick}
      />
    ),
    [handleWeekPointClick, weeklyChartData]
  );

  const allCountLabelContent = useCallback(
    (labelProps) => (
      <WeeklyTrendCountLabel
        viewBox={labelProps.viewBox}
        position={labelProps.position}
        offset={labelProps.offset}
        value={labelProps.value}
        index={labelProps.index}
        x={labelProps.x}
        y={labelProps.y}
        fill={ALL_MIGRATIONS_TREND_COLOR}
        fontSize={12}
        fontWeight={700}
        mode="all"
        chartRows={weeklyChartData}
        onWeekClick={handleWeekPointClick}
      />
    ),
    [handleWeekPointClick, weeklyChartData]
  );

  const openDetails = (row) => {
    setDialogRow(row);
    setDialogOpen(true);
  };

  /** Snapshot: attempt outcomes + path-level "fast run" metrics (% of migration paths). */
  const snapshotSummary = useMemo(() => {
    const migrations = payload?.migrations;
    if (!migrations || !Array.isArray(migrations) || migrations.length === 0) return null;

    const { fromMs, toMs, windowLabel, cutoffLabel } = periodRange;

    const windowMigrations = buildMigrationsForHealthWindow(migrations, fromMs, toMs);

    const MS_HOUR = 60 * 60 * 1000;
    const MS_3H = 3 * MS_HOUR;

    if (windowMigrations.length === 0) {
      return {
        isEmptyWindow: true,
        windowLabel,
        cutoffLabel,
        totalPaths: 0,
        totalAttempts: 0,
        totalMs: 0,
        outcomeBuckets: null,
        durationThresholdRows: [],
        maxAttemptCount: 0,
        pathFinishBar: [],
        pathSuccessPct: 0,
        firstTryReliabilityPct: 0,
        medianSuccessDurationMs: null,
        pctSuccessUnder3h: 0,
        avgAttemptsPerPath: 0
      };
    }

    const totalPaths = windowMigrations.length;
    const pctOfPaths = (n) => (totalPaths > 0 ? Math.round((n / totalPaths) * 1000) / 10 : 0);
    const pathAttemptCount = (m) =>
      m.attemptCount != null ? Number(m.attemptCount) : (m.attempts || []).length;


    let maxAttemptCount = 0;
    for (const m of windowMigrations) {
      const ac = pathAttemptCount(m);
      if (ac > maxAttemptCount) maxAttemptCount = ac;
    }
    const kMax = Math.min(Math.max(maxAttemptCount, 1), 20);

    const durationThresholdRows = [];
    for (let k = 1; k <= kMax; k++) {
      const under3hCount = windowMigrations.filter((m) => pathAttemptCount(m) === k && pathTotalMs(m) < MS_3H).length;
      const limitMs = k * MS_HOUR;
      const underKHrsCount = windowMigrations.filter((m) => pathAttemptCount(m) === k && pathTotalMs(m) < limitMs).length;
      durationThresholdRows.push({
        k,
        under3hCount,
        under3hPct: pctOfPaths(under3hCount),
        underKHrsCount,
        underKHrsPct: pctOfPaths(underKHrsCount)
      });
    }

    let totalAttempts = 0;
    let totalMs = 0;
    const acc = {
      success: { n: 0, ms: 0 },
      failure: { n: 0, ms: 0 },
      other: { n: 0, ms: 0 }
    };
    for (const m of windowMigrations) {
      for (const a of m.attempts || []) {
        totalAttempts += 1;
        const ms = Number(a.durationMs) || 0;
        totalMs += ms;
        let key = 'other';
        if (a.status === 'completed') {
          if (a.conclusion === 'success') key = 'success';
          else if (a.conclusion === 'failure') key = 'failure';
        }
        acc[key].n += 1;
        acc[key].ms += ms;
      }
    }

    const pct = (x, tot) => (tot > 0 ? Math.round((x / tot) * 1000) / 10 : 0);
    const outcomeBuckets =
      totalAttempts > 0
        ? [
            {
              key: 'success',
              label: 'Success',
              attempts: acc.success.n,
              durationMs: acc.success.ms,
              pctAttempts: pct(acc.success.n, totalAttempts),
              pctDuration: pct(acc.success.ms, totalMs)
            },
            {
              key: 'failure',
              label: 'Failure',
              attempts: acc.failure.n,
              durationMs: acc.failure.ms,
              pctAttempts: pct(acc.failure.n, totalAttempts),
              pctDuration: pct(acc.failure.ms, totalMs)
            },
            {
              key: 'other',
              label: 'Other (cancelled, skipped, in progress, ...)',
              attempts: acc.other.n,
              durationMs: acc.other.ms,
              pctAttempts: pct(acc.other.n, totalAttempts),
              pctDuration: pct(acc.other.ms, totalMs)
            }
          ]
        : null;

    let pathSuccess = 0;
    let pathFailure = 0;
    let pathOther = 0;
    let firstTrySuccess = 0;
    const successPathDurations = [];
    let successUnder3h = 0;
    for (const m of windowMigrations) {
      const ac = pathAttemptCount(m);
      const conc = m.latestConclusion;
      const tms = pathTotalMs(m);
      if (conc === 'success') {
        pathSuccess += 1;
        successPathDurations.push(tms);
        if (tms < MS_3H) successUnder3h += 1;
        if (ac === 1) firstTrySuccess += 1;
      } else if (conc === 'failure') {
        pathFailure += 1;
      } else {
        pathOther += 1;
      }
    }
    const pathFinishBar = [
      {
        key: 'success',
        label: 'Success',
        count: pathSuccess,
        pct: pct(pathSuccess, totalPaths),
        color: 'success.main'
      },
      {
        key: 'failure',
        label: 'Failure',
        count: pathFailure,
        pct: pct(pathFailure, totalPaths),
        color: 'error.main'
      },
      {
        key: 'other',
        label: 'Other',
        count: pathOther,
        pct: pct(pathOther, totalPaths),
        color: 'grey.500'
      }
    ].filter((s) => s.pct > 0 || s.count > 0);

    const medianSuccessDurationMs = medianMs(successPathDurations);
    const pctSuccessUnder3h = pathSuccess > 0 ? pct(successUnder3h, pathSuccess) : 0;
    const firstTryReliabilityPct = totalPaths > 0 ? pct(firstTrySuccess, totalPaths) : 0;
    const pathSuccessPct = pct(pathSuccess, totalPaths);
    const avgAttemptsPerPath = totalPaths > 0 ? Math.round((totalAttempts / totalPaths) * 10) / 10 : 0;

    return {
      isEmptyWindow: false,
      windowLabel,
      cutoffLabel,
      totalPaths,
      totalAttempts,
      totalMs,
      outcomeBuckets,
      durationThresholdRows,
      maxAttemptCount,
      pathFinishBar,
      pathSuccessPct,
      firstTryReliabilityPct,
      medianSuccessDurationMs,
      pctSuccessUnder3h,
      avgAttemptsPerPath
    };
  }, [payload?.migrations, periodRange]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: AZURE_COLOR }}>
            {isProvisioning
              ? 'GitHub Actions - instance provisioning (himat)'
              : 'GitHub Actions - cut-over migration workflows'}
          </Typography>
          <IconButton size="small" aria-label="About this page" onClick={() => setHelpOpen(true)} sx={{ color: 'text.secondary' }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          {meta.lastSyncedAt && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Last sync: {formatDateTime(meta.lastSyncedAt)}
                {meta.syncedBy?.username && (
                  <span> · by {meta.syncedBy.username}</span>
                )}
              </Typography>
              {payload?.workflowRunsPageLimit != null && payload.workflowRunsPageLimit < GITHUB_SYNC_PAGES_FULL && (
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360, textAlign: 'right' }}>
                  Board uses up to {payload.workflowRunsPageLimit} page(s) of GitHub workflow runs (~
                  {payload.workflowRunsPageLimit * 100} runs max). Use <strong>Full history</strong> for more (slower).
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <MuiTooltip
              title="Fetches only runs newer than the last sync and merges them in - no data wipe. Fastest option for routine updates."
              placement="bottom"
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={incrementalSyncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                  onClick={handleIncrementalSync}
                  disabled={loading || syncLoading || incrementalSyncLoading}
                  sx={{ bgcolor: AZURE_COLOR, '&:hover': { bgcolor: '#153328' }, textTransform: 'none' }}
                >
                  {incrementalSyncLoading ? 'Syncing...' : 'Sync (new only)'}
                </Button>
              </span>
            </MuiTooltip>
            <MuiTooltip
              title={`Loads up to ${GITHUB_SYNC_PAGES_RECENT} pages (~${GITHUB_SYNC_PAGES_RECENT * 100} workflow runs), newest first. Use this if syncs fail with "failed to fetch" (often a gateway timeout).`}
              placement="bottom"
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                  onClick={() => handleFreshSync(GITHUB_SYNC_PAGES_RECENT)}
                  disabled={loading || syncLoading || incrementalSyncLoading}
                  sx={{ bgcolor: AZURE_COLOR, '&:hover': { bgcolor: '#153328' }, textTransform: 'none' }}
                >
                  {syncLoading ? 'Syncing...' : 'Fresh sync (recent)'}
                </Button>
              </span>
            </MuiTooltip>
            <MuiTooltip
              title={`Up to ${GITHUB_SYNC_PAGES_FULL} pages (up to ${GITHUB_SYNC_PAGES_FULL * 100} runs). Much slower; may still hit proxy timeouts.`}
              placement="bottom"
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={syncLoading ? <CircularProgress size={16} sx={{ color: AZURE_COLOR }} /> : <SyncIcon />}
                  onClick={() => handleFreshSync(GITHUB_SYNC_PAGES_FULL)}
                  disabled={loading || syncLoading || incrementalSyncLoading}
                  sx={{
                    textTransform: 'none',
                    borderColor: AZURE_COLOR,
                    color: AZURE_COLOR,
                    '&:hover': { borderColor: '#153328', bgcolor: 'action.hover' }
                  }}
                >
                  Full history
                </Button>
              </span>
            </MuiTooltip>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={clearMongoLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            onClick={() => setClearMongoDialogOpen(true)}
            disabled={
              loading ||
              syncLoading ||
              incrementalSyncLoading ||
              clearMongoLoading ||
              !!pathRefreshKey ||
              filteredPathsRefreshLoading
            }
            sx={{ textTransform: 'none' }}
          >
            Clean Data
          </Button>
          {payload?.workflowUrl && (
            <Link href={payload.workflowUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              View workflow on GitHub
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </Link>
          )}
        </Box>
      </Box>

      {syncLoading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {SYNC_STATUS_HINTS[syncHintIndex]}
          </Typography>
        </Box>
      )}

      {syncBanner && (
        <Alert severity="success" onClose={() => setSyncBanner(null)} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {syncBanner.summary}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Sync steps (last 15; full detail in server logs: <code>[migrationWorkflowSync]</code>)
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              mt: 1,
              pl: 2.5,
              maxHeight: 220,
              overflow: 'auto',
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: '0.7rem'
            }}
          >
            {syncBanner.phases.slice(-15).map((p, idx) => (
              <li key={`${p.step}-${idx}`}>
                {p.step}
                {p.detail ? `: ${p.detail}` : ''}
                {p.elapsedMs != null ? ` (${p.elapsedMs}ms)` : ''}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {!loading && payload && snapshotSummary && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            overflow: 'hidden',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.action.hover} 0%, ${theme.palette.background.paper} 48%, ${theme.palette.action.selected} 100%)`
          }}
        >
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2
              }}
            >
              <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: AZURE_COLOR, letterSpacing: 0.3 }}>
                  Migration health (snapshot)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  How paths <strong>finish</strong> (latest attempt in period), <strong>reliability</strong> (success & first-try), and{' '}
                  <strong>time</strong> (median & under 3h for successes).{' '}
                  {healthPeriodMode === 'relative'
                    ? <>Only attempts <strong>started</strong> on or after {snapshotSummary.cutoffLabel} are included ({snapshotSummary.windowLabel} window).</>
                    : <>Only attempts <strong>started</strong> {snapshotSummary.windowLabel} are included.</>
                  }
                </Typography>
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="health-period-mode-label">Period type</InputLabel>
                    <Select
                      labelId="health-period-mode-label"
                      label="Period type"
                      value={healthPeriodMode}
                      onChange={(e) => setHealthPeriodMode(e.target.value)}
                    >
                      <MenuItem value="relative">Relative period</MenuItem>
                      <MenuItem value="dateRange">Date range</MenuItem>
                      <MenuItem value="priorTo">Prior to date</MenuItem>
                    </Select>
                  </FormControl>
                  {healthPeriodMode === 'relative' && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel id="health-snap-window-label">Period</InputLabel>
                      <Select
                        labelId="health-snap-window-label"
                        label="Period"
                        value={healthSnapshotWindowMonths}
                        onChange={(e) => setHealthSnapshotWindowMonths(e.target.value)}
                      >
                        <MenuItem value="7d">Last 7 days</MenuItem>
                        <MenuItem value="15d">Last 15 days</MenuItem>
                        <MenuItem value="30d">Last month (30 days)</MenuItem>
                        <MenuItem value="3m">Last 3 months</MenuItem>
                        <MenuItem value="6m">Last 6 months</MenuItem>
                        <MenuItem value="12m">Last 12 months</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {healthPeriodMode === 'dateRange' && (
                    <>
                      <DatePicker
                        label="From date"
                        value={healthDateFrom}
                        onChange={(v) => setHealthDateFrom(v)}
                        slotProps={{
                          textField: { size: 'small', sx: { minWidth: 145 } },
                          actionBar: { actions: ['clear', 'today'] }
                        }}
                      />
                      <DatePicker
                        label="To date"
                        value={healthDateTo}
                        onChange={(v) => setHealthDateTo(v)}
                        slotProps={{
                          textField: { size: 'small', sx: { minWidth: 145 } },
                          actionBar: { actions: ['clear', 'today'] }
                        }}
                      />
                    </>
                  )}
                  {healthPeriodMode === 'priorTo' && (
                    <DatePicker
                      label="Prior to"
                      value={healthDateTo}
                      onChange={(v) => setHealthDateTo(v)}
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 160 } },
                        actionBar: { actions: ['clear', 'today'] }
                      }}
                    />
                  )}
                </Box>
              </LocalizationProvider>
            </Box>

            {snapshotSummary.isEmptyWindow ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No migration paths had workflow activity in this period ({snapshotSummary.windowLabel}). Try a different period or refresh the snapshot from GitHub.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  {snapshotSummary.totalPaths.toLocaleString()} paths · {snapshotSummary.totalAttempts.toLocaleString()} attempts ·{' '}
                  {formatDurationMs(snapshotSummary.totalMs)} run time
                </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 1.5,
                mt: 2
              }}
            >
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Finish (success)
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: 'success.main', lineHeight: 1.2 }}>
                  {snapshotSummary.pathSuccessPct}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  paths with latest in-period attempt succeeded
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  First-try OK
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: AZURE_COLOR, lineHeight: 1.2 }}>
                  {snapshotSummary.firstTryReliabilityPct}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  1 attempt and success
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Typical time
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                  {snapshotSummary.medianSuccessDurationMs != null
                    ? formatDurationMs(snapshotSummary.medianSuccessDurationMs)
                    : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  median total time (success paths)
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Under 3h
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                  {snapshotSummary.pctSuccessUnder3h}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of success paths · {snapshotSummary.avgAttemptsPerPath} att/path avg
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                Where paths land (latest conclusion)
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  height: 16,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`
                }}
              >
                {snapshotSummary.pathFinishBar.length === 0 ? (
                  <Box sx={{ flex: 1, bgcolor: 'grey.300' }} />
                ) : (
                  snapshotSummary.pathFinishBar.map((s) => (
                    <Box
                      key={s.key}
                      title={`${s.label}: ${s.count} paths (${s.pct}%)`}
                      sx={{
                        flex: s.pct > 0 ? s.pct : 0,
                        minWidth: s.count > 0 ? 6 : 0,
                        bgcolor: s.color,
                        transition: 'flex 0.35s ease'
                      }}
                    />
                  ))
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                {snapshotSummary.pathFinishBar.map((s) => (
                  <Chip
                    key={s.key}
                    size="small"
                    variant="outlined"
                    label={`${s.label} ${s.pct}% (${s.count})`}
                    sx={{
                      borderColor: s.color,
                      '& .MuiChip-label': { fontWeight: 600, fontSize: '0.7rem' }
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Detailed breakdown (attempt outcomes and duration)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Table percentages use <strong>migration paths</strong> as 100%.{' '}
                Per-path duration is the sum of in-period attempts on that path.
              </Typography>
              {snapshotSummary.outcomeBuckets && (
                <>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                    By attempt outcome (each attempt)
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                      gap: 1.5,
                      mb: 2
                    }}
                  >
                    {snapshotSummary.outcomeBuckets.map((b) => (
                      <Box
                        key={b.key}
                        sx={{
                          borderLeft: 3,
                          borderColor: b.key === 'success' ? 'success.main' : b.key === 'failure' ? 'error.main' : 'grey.400',
                          pl: 1.5
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {b.label}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{b.attempts.toLocaleString()}</strong> attempts ({b.pctAttempts}% of attempts)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDurationMs(b.durationMs)} ({b.pctDuration}% of attempt time)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
                Paths by attempt count - total duration vs thresholds
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Each row: paths with exactly <em>k</em> attempts.{' '}
                Per-path duration is the sum of all attempts. Compare total run time to a fixed 3h cap and to <em>k</em> hours (same <em>k</em> as the row).
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 960 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Attempts (k)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Total &lt; 3h
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        % paths
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Total &lt; k h
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        % paths
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {snapshotSummary.durationThresholdRows.map((row) => (
                      <TableRow key={`dur-${row.k}`}>
                        <TableCell>
                          Exactly {row.k} attempt{row.k === 1 ? '' : 's'}
                        </TableCell>
                        <TableCell align="right">{row.under3hCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.under3hPct}%</TableCell>
                        <TableCell align="right">{row.underKHrsCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.underKHrsPct}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
              </>
            )}
          </Box>
        </Paper>
      )}

      {!loading && payload && (payload.migrations || []).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: AZURE_COLOR, mb: 1 }}>
            Weekly trend
          </Typography>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: AZURE_COLOR }}>
            Chart filters
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="mw-max-hours-label">Under (hours)</InputLabel>
              <Select
                labelId="mw-max-hours-label"
                label="Under (hours)"
                value={maxHoursFilter}
                onChange={(e) => setMaxHoursFilter(Number(e.target.value))}
              >
                {HOUR_PRESETS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {`< ${h} hours`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="mw-ac-label">Attempts</InputLabel>
              <Select
                labelId="mw-ac-label"
                label="Attempts"
                value={attemptCountFilter}
                onChange={(e) => setAttemptCountFilter(e.target.value)}
              >
                {ATTEMPT_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
              <InputLabel id="mw-dc-label" shrink>
                Source datacenter
              </InputLabel>
              <Select
                labelId="mw-dc-label"
                label="Source datacenter"
                value={sourceDcFilter}
                onChange={(e) => setSourceDcFilter(e.target.value)}
                displayEmpty
                renderValue={(selected) =>
                  selected === '' || selected == null ? (
                    <span>All datacenters</span>
                  ) : (
                    selected
                  )
                }
              >
                <MenuItem value="">
                  <em>All datacenters</em>
                </MenuItem>
                {sourceDcOptions.map((dc) => (
                  <MenuItem key={dc} value={dc}>
                    {dc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {weeklyChartData.length > 0 ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={weeklyChartData} margin={{ top: 28, right: 12, left: 4, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} label={{ value: 'Clients', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(w) => `Week of ${w} (click a point or count for details)`}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="filteredCount"
                    name="Filtered (current rules)"
                    stroke={AZURE_COLOR}
                    strokeWidth={2}
                    dot={(dotProps) => {
                      const { cx, cy, payload } = dotProps;
                      if (cx == null || cy == null) return null;
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill={AZURE_COLOR}
                          stroke="#fff"
                          strokeWidth={2}
                          style={{ cursor: 'pointer' }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleWeekPointClick(payload, 'filtered');
                          }}
                        />
                      );
                    }}
                    activeDot={{ r: 7 }}
                  >
                    <LabelList
                      dataKey="filteredCount"
                      position="top"
                      offset={8}
                      content={filteredCountLabelContent}
                    />
                  </Line>
                  <Line
                    type="monotone"
                    dataKey="allCount"
                    name="All migrations"
                    stroke={ALL_MIGRATIONS_TREND_COLOR}
                    strokeWidth={2.5}
                    dot={(dotProps) => {
                      const { cx, cy, payload } = dotProps;
                      if (cx == null || cy == null) return null;
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={6}
                          fill={ALL_MIGRATIONS_TREND_COLOR}
                          stroke="#fff"
                          strokeWidth={2}
                          style={{ cursor: 'pointer' }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleWeekPointClick(payload, 'all');
                          }}
                        />
                      );
                    }}
                    activeDot={{ r: 8 }}
                  >
                    <LabelList
                      dataKey="allCount"
                      position="top"
                      offset={22}
                      content={allCountLabelContent}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No weekly buckets could be built from migration attempts. Refresh snapshot data after workflows run.
            </Alert>
          )}
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && !payload && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No workflow snapshot stored yet. Click <strong>Fresh sync (recent)</strong> to fetch the latest workflow runs from Actions
          and save them to the metrics database (recommended). Use <strong>Full history</strong> only if you need older runs and
          can wait; it may time out behind a proxy.
        </Alert>
      )}

      {!loading && payload && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'flex-end'
                }}
              >
                <DatePicker
                  label="Created from"
                  value={tableDateFrom}
                  onChange={(v) => setTableDateFrom(v)}
                  slotProps={{
                    textField: { size: 'small', sx: { minWidth: 160 } },
                    actionBar: { actions: ['clear', 'today'] }
                  }}
                />
                <DatePicker
                  label="Created to"
                  value={tableDateTo}
                  onChange={(v) => setTableDateTo(v)}
                  slotProps={{
                    textField: { size: 'small', sx: { minWidth: 160 } },
                    actionBar: { actions: ['clear', 'today'] }
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="mw-tbl-status-label">Latest status</InputLabel>
                  <Select
                    labelId="mw-tbl-status-label"
                    label="Latest status"
                    value={tableStatus}
                    onChange={(e) => setTableStatus(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="failure">Failure</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="skipped">Skipped</MenuItem>
                    <MenuItem value="not_completed">Not completed</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Client contains"
                  value={tableClientSearch}
                  onChange={(e) => setTableClientSearch(e.target.value)}
                  sx={{ minWidth: 160 }}
                />
                <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="mw-tbl-src-label" shrink>
                    Source DC
                  </InputLabel>
                  <Select
                    labelId="mw-tbl-src-label"
                    label="Source DC"
                    value={tableSourceDc}
                    onChange={(e) => setTableSourceDc(e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      selected === '' || selected == null ? <span>All</span> : selected
                    }
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {sourceDcOptions.map((dc) => (
                      <MenuItem key={`src-${dc}`} value={dc}>
                        {dc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="mw-tbl-tgt-label" shrink>
                    Target DC
                  </InputLabel>
                  <Select
                    labelId="mw-tbl-tgt-label"
                    label="Target DC"
                    value={tableTargetDc}
                    onChange={(e) => setTableTargetDc(e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      selected === '' || selected == null ? <span>All</span> : selected
                    }
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {targetDcOptions.map((dc) => (
                      <MenuItem key={`tgt-${dc}`} value={dc}>
                        {dc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label="Min duration (h)"
                  value={tableMinDurH}
                  onChange={(e) => setTableMinDurH(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ width: 130 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max duration (h)"
                  value={tableMaxDurH}
                  onChange={(e) => setTableMaxDurH(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ width: 130 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Min attempts"
                  value={tableMinAttempts}
                  onChange={(e) => setTableMinAttempts(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: 110 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max attempts"
                  value={tableMaxAttempts}
                  onChange={(e) => setTableMaxAttempts(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: 110 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearTableFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Clear table filters
                </Button>
                <MuiTooltip title="Re-fetch all rows currently matching the table filters from GitHub (one batch; max 200 paths)">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        filteredPathsRefreshLoading ? <CircularProgress size={16} /> : <SyncIcon />
                      }
                      onClick={handleRefreshFilteredPaths}
                      disabled={
                        filteredMigrations.length === 0 ||
                        filteredPathsRefreshLoading ||
                        syncLoading ||
                        !!pathRefreshKey
                      }
                      sx={{ textTransform: 'none', borderColor: AZURE_COLOR, color: AZURE_COLOR }}
                    >
                      {filteredPathsRefreshLoading ? 'Refreshing...' : 'Refresh filtered'}
                    </Button>
                  </span>
                </MuiTooltip>
              </Box>
            </LocalizationProvider>
            {pathsRefreshBanner && (
              <Alert
                severity={pathsRefreshBanner.severity}
                onClose={() => setPathsRefreshBanner(null)}
                sx={{ mt: 1.5 }}
              >
                {pathsRefreshBanner.message}
              </Alert>
            )}
          </Paper>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source DC</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Target DC</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Latest status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Open</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total duration
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Attempts
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(payload.migrations || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ color: 'text.secondary' }}>
                      No workflow runs matched the expected title pattern.
                    </TableCell>
                  </TableRow>
                ) : filteredMigrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ color: 'text.secondary' }}>
                      No migration paths match the current table filters. Widen the criteria or click <strong>Clear table filters</strong>.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMigrations.map((row) => {
                    const st = row.latestStatus;
                    const conc = row.latestConclusion;
                    const created =
                      row.latestRunCreatedAt ||
                      (Array.isArray(row.attempts) && row.attempts[0]?.runCreatedAt) ||
                      null;
                    const openUrl = row.latestHtmlUrl || (Array.isArray(row.attempts) && row.attempts[0]?.htmlUrl) || null;
                    return (
                      <TableRow key={`${row.clientName}|${row.sourceDatacenter}|${row.targetDatacenter}`}>
                        <TableCell>{row.clientName}</TableCell>
                        <TableCell>{row.sourceDatacenter}</TableCell>
                        <TableCell>{row.targetDatacenter}</TableCell>
                        <TableCell>
                          {st === 'completed' ? (
                            <Chip
                              size="small"
                              label={conc || '-'}
                              color={conc === 'success' ? 'success' : conc === 'failure' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          ) : (
                            <Chip size="small" label={st || '-'} color="warning" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(created)}</TableCell>
                        <TableCell>
                          {openUrl ? (
                            <Link href={openUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              Open
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="right">{formatDurationMs(pathTotalMs(row))}</TableCell>
                        <TableCell align="right">{row.attemptCount}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                            <MuiTooltip title="Re-fetch this path from GitHub (updates attempts without a full sync)">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Refresh this migration path from GitHub"
                                  disabled={!!pathRefreshKey || syncLoading || filteredPathsRefreshLoading}
                                  onClick={() => handleRefreshPath(row)}
                                  sx={{ color: AZURE_COLOR }}
                                >
                                  {pathRefreshKey === migrationPathKey(row) ? (
                                    <CircularProgress size={18} sx={{ color: AZURE_COLOR }} />
                                  ) : (
                                    <SyncIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </MuiTooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openDetails(row)}
                              sx={{ textTransform: 'none', borderColor: AZURE_COLOR, color: AZURE_COLOR }}
                            >
                              More info
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            {filteredMigrations.length > 0 && (
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                count={filteredMigrations.length}
                rowsPerPage={tableRowsPerPage}
                page={tablePage}
                onPageChange={(_, newPage) => setTablePage(newPage)}
                onRowsPerPageChange={(e) => {
                  setTableRowsPerPage(parseInt(e.target.value, 10));
                  setTablePage(0);
                }}
                labelRowsPerPage="Rows per page:"
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            )}
          </TableContainer>
          {(payload.unparsedCount ?? payload.unparsedRuns?.length ?? 0) > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: AZURE_COLOR, mb: 1 }}>
                Runs that did not match the title pattern
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Run #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Title used for match</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>display_title</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>GitHub</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUnparsedRuns.map((u) => (
                      <TableRow key={`unparsed-${u.id}-${u.runNumber}`}>
                        <TableCell>{u.runNumber ?? '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>{u.title}</TableCell>
                        <TableCell sx={{ maxWidth: 200, wordBreak: 'break-word', color: 'text.secondary', fontSize: '0.8rem' }}>
                          {u.displayTitle != null ? u.displayTitle : '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160, wordBreak: 'break-word', color: 'text.secondary', fontSize: '0.8rem' }}>
                          {u.workflowRunName != null ? u.workflowRunName : '-'}
                        </TableCell>
                        <TableCell>{u.event || '-'}</TableCell>
                        <TableCell>{u.headBranch || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(u.createdAt)}</TableCell>
                        <TableCell>
                          {u.status === 'completed' ? (
                            <Chip size="small" label={u.conclusion || '-'} color={u.conclusion === 'success' ? 'success' : u.conclusion === 'failure' ? 'error' : 'default'} variant="outlined" />
                          ) : (
                            <Chip size="small" label={u.status || '-'} color="warning" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {u.htmlUrl ? (
                            <Link href={u.htmlUrl} target="_blank" rel="noopener noreferrer">
                              Open
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  count={unparsedRunsList.length}
                  rowsPerPage={unparsedRowsPerPage}
                  page={unparsedPage}
                  onPageChange={(_, newPage) => setUnparsedPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setUnparsedRowsPerPage(parseInt(e.target.value, 10));
                    setUnparsedPage(0);
                  }}
                  labelRowsPerPage="Rows per page:"
                  sx={{ borderTop: 1, borderColor: 'divider' }}
                />
              </TableContainer>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={clearMongoDialogOpen}
        onClose={() => !clearMongoLoading && setClearMongoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isProvisioning ? 'Clear instance provisioning workflow statistics?' : 'Clear Azure migration statistics?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This deletes the cached <strong>GitHub Actions</strong> workflow snapshot for{' '}
            {isProvisioning ? (
              <strong>instance provisioning</strong>
            ) : (
              <>
                Azure <strong>cut-over migration</strong> metrics
              </>
            )}{' '}
            from MongoDB. Uploaded Excel migration data and other migration tabs are <strong>not</strong> removed.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            Use <strong>Fresh sync (recent)</strong> or <strong>Full history</strong> afterward to load statistics again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearMongoDialogOpen(false)} disabled={clearMongoLoading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmClearMongo}
            disabled={clearMongoLoading}
            sx={{ textTransform: 'none' }}
          >
            {clearMongoLoading ? 'Clearing...' : 'Clear stored data'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
          About this page
          <IconButton size="small" aria-label="Close" onClick={() => setHelpOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Data source
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Data is stored in MongoDB. Use <strong>Fresh sync (recent)</strong> for a fast pull (a few hundred of the newest workflow
            runs), or <strong>Full history</strong> for up to many thousands of runs (slow; may fail if a gateway times out). Both
            replace the snapshot with a live pull from Actions.
            {payload ? (
              <>
                {' '}
                Config: branch <strong>{payload.branch || 'main'}</strong>, repo <strong>{payload.repo || 'tr/highq_migration'}</strong>, workflow{' '}
                <strong>{payload.workflow || 'highq-migration-main.yml'}</strong>.
              </>
            ) : (
              ' '
            )}
            {isProvisioning ? (
              <>
                Only workflow titles matching{' '}
                <code style={{ fontSize: '0.85em' }}>
                  HiMAT instance provisioning: &lt;client&gt; S: &lt;source DC&gt; D: &lt;target DC&gt;
                </code>{' '}
                (for example{' '}
                <code style={{ fontSize: '0.85em' }}>HiMAT instance provisioning: millsandreeve S: sg2 D: prd1zuk1</code>) are included.
                Multiple workflow runs with the same client and source/target DC are merged into one row; each run (and each re-run
                attempt on that run) counts as an <strong>attempt</strong> on that path. Other titles are listed under &quot;Runs that did
                not match the title pattern&quot;. Total duration is the <strong>sum</strong> of wall-clock durations per attempt (
                <code>run_attempt</code>).
              </>
            ) : (
              <>
                Only workflow titles matching{' '}
                <code style={{ fontSize: '0.85em' }}>HighQ Migration: &lt;client&gt; - &lt;source DB&gt; - &lt;target DB&gt;</code> (for example{' '}
                <code style={{ fontSize: '0.85em' }}>HighQ Migration: partnerey - usa2 - prd1zus1</code>) are included in this report. Titles such as{' '}
                <code style={{ fontSize: '0.85em' }}>HighQ Migration: &lt;client&gt;</code> without source/target segments are <strong>ignored</strong> here and listed under &quot;Runs that did not match the title pattern&quot;. Total
                duration is the <strong>sum</strong> of wall-clock durations per workflow attempt (<code>run_attempt</code>).
              </>
            )}
          </Typography>

          {payload && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Snapshot
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {payload.totalRunsFetched?.toLocaleString() ?? '0'} run(s) from GitHub when synced;{' '}
                {payload.migrations?.length ?? 0} unique client / datacenter path(s).
                {(payload.migrations?.length ?? 0) > 0 && filteredMigrations.length !== (payload.migrations?.length ?? 0) && (
                  <>
                    {' '}
                    Table filters: showing <strong>{filteredMigrations.length}</strong> row(s).
                  </>
                )}
              </Typography>
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Weekly chart
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>All migrations</strong> (blue line) counts every client path with attempts per week (latest attempt).{' '}
            <strong>Filtered</strong> (green line) uses the chart filters: total duration under the hour limit, attempts, and
            source datacenter. Week = Monday week of the latest attempt start. Click a point to open that week&apos;s client list.
          </Typography>
          {(payload?.migrations || []).length > 0 && (
            <Typography variant="body2" color="text.secondary" paragraph sx={{ pl: 0 }}>
              <strong>Current filtered line</strong>: total duration &lt; <strong>{maxHoursFilter} h</strong>
              {attemptCountFilter !== 'any' && (
                <>
                  {' '}
                  · Attempts: <strong>{ATTEMPT_FILTER_OPTIONS.find((x) => x.value === attemptCountFilter)?.label}</strong>
                </>
              )}
              {attemptCountFilter === 'any' && <> · Attempts: any</>}
              {sourceDcFilter ? (
                <>
                  {' '}
                  · Source DC: <strong>{sourceDcFilter}</strong>
                </>
              ) : (
                <> · All source datacenters</>
              )}
              . The blue line has no chart filters.
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Table filters
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Narrow the table by latest run <strong>created</strong> date, <strong>status</strong>, total <strong>duration</strong>{' '}
            (hours), <strong>attempts</strong>, client name, and source/target datacenters. These do not change the weekly chart.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Unparsed runs
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Runs whose titles did not match the expected pattern are listed separately. Compare columns to spot naming differences.
            {payload && (payload.unparsedCount ?? 0) > 0 && (
              <>
                {' '}
                Showing {payload.unparsedRuns?.length ?? 0} of {payload.unparsedCount ?? payload.unparsedRuns?.length ?? 0}{' '}
                non-matching run(s)
                {(payload.unparsedCount ?? 0) > (payload.unparsedRuns?.length ?? 0) && ' (list capped at 500)'}.
              </>
            )}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={weekPointOpen} onClose={() => setWeekPointOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
          <Box>
            <Typography variant="h6">Week of {weekPointDetail?.week || '-'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {weekPointDetail?.clientCount ?? 0} client(s)
              {weekPointDetail?.mode === 'all' ? (
                <> · all migrations (no chart filters)</>
              ) : (
                <>
                  {' '}
                  · filtered: &lt; {maxHoursFilter} h
                  {attemptCountFilter !== 'any'
                    ? `, ${attemptCountFilter === '5+' ? '5+' : attemptCountFilter} attempt(s)`
                    : ', any attempts'}
                  {sourceDcFilter ? `, ${sourceDcFilter}` : ''}
                </>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <MuiTooltip title="Export visible rows to CSV">
              <span>
                <IconButton
                  size="small"
                  aria-label="Export to CSV"
                  onClick={exportWeekItemsCsv}
                  disabled={!sortedWeekItems.length}
                  sx={{ color: AZURE_COLOR }}
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </MuiTooltip>
            <IconButton size="small" onClick={() => setWeekPointOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {sortedWeekItems.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={weekModalSortKey === 'clientName'}
                        direction={weekModalSortKey === 'clientName' ? weekModalSortDir : 'asc'}
                        onClick={() => handleWeekModalSort('clientName')}
                      >
                        Client
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={weekModalSortKey === 'sourceDatacenter'}
                        direction={weekModalSortKey === 'sourceDatacenter' ? weekModalSortDir : 'asc'}
                        onClick={() => handleWeekModalSort('sourceDatacenter')}
                      >
                        Source DC
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Target DC</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Latest status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={weekModalSortKey === 'attemptCount'}
                        direction={weekModalSortKey === 'attemptCount' ? weekModalSortDir : 'desc'}
                        onClick={() => handleWeekModalSort('attemptCount')}
                      >
                        Attempts
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={weekModalSortKey === 'totalDurationMs'}
                        direction={weekModalSortKey === 'totalDurationMs' ? weekModalSortDir : 'desc'}
                        onClick={() => handleWeekModalSort('totalDurationMs')}
                      >
                        Total duration
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Open</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedWeekItems.map((r, idx) => {
                    const st = r.latestStatus;
                    const conc = r.latestConclusion;
                    return (
                    <TableRow key={`${r.clientName}-${r.sourceDatacenter}-${idx}`}>
                      <TableCell>{r.clientName}</TableCell>
                      <TableCell>{r.sourceDatacenter}</TableCell>
                      <TableCell>{r.targetDatacenter}</TableCell>
                      <TableCell>
                        {st === 'completed' ? (
                          <Chip
                            size="small"
                            label={conc || '-'}
                            color={conc === 'success' ? 'success' : conc === 'failure' ? 'error' : 'default'}
                            variant="outlined"
                          />
                        ) : (
                          <Chip size="small" label={st || '-'} color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">{r.attemptCount}</TableCell>
                      <TableCell align="right">{formatDurationMs(pathTotalMs(r))}</TableCell>
                      <TableCell>
                        {r.latestHtmlUrl ? (
                          <Link href={r.latestHtmlUrl} target="_blank" rel="noopener noreferrer">
                            Open
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No clients for this week.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', pb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography variant="h6">Migration attempts</Typography>
            {dialogRow && (
              <Typography variant="body2" color="text.secondary">
                {dialogRow.clientName} · {dialogRow.sourceDatacenter} → {dialogRow.targetDatacenter}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {dialogRow && (
              <Button
                size="small"
                variant="outlined"
                startIcon={pathRefreshKey === migrationPathKey(dialogRow) ? <CircularProgress size={14} /> : <SyncIcon />}
                disabled={!!pathRefreshKey || syncLoading || filteredPathsRefreshLoading}
                onClick={() => handleRefreshPath(dialogRow)}
                sx={{ textTransform: 'none', borderColor: AZURE_COLOR, color: AZURE_COLOR }}
              >
                Refresh from GitHub
              </Button>
            )}
            <IconButton size="small" onClick={() => setDialogOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {dialogRow && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Total duration (sum of attempts): <strong>{formatDurationMs(dialogRow.totalDurationMs)}</strong> ·{' '}
                <strong>{dialogRow.attemptCount}</strong> attempt(s)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Run #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Attempt</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Open</TableCell>
                </TableRow>
                  </TableHead>
                  <TableBody>
                    {dialogRow.attempts.map((a) => (
                      <TableRow key={`${a.id}-${a.workflowAttemptNumber ?? a.runNumber}`}>
                        <TableCell>{a.runNumber}</TableCell>
                        <TableCell>{a.workflowAttemptNumber ?? '-'}</TableCell>
                        <TableCell>{formatDateTime(a.startedAt)}</TableCell>
                        <TableCell>{formatDateTime(a.endedAt)}</TableCell>
                        <TableCell>{formatDurationMs(a.durationMs)}</TableCell>
                        <TableCell>
                          {a.status === 'completed' ? (
                            <Chip size="small" label={a.conclusion || '-'} color={a.conclusion === 'success' ? 'success' : a.conclusion === 'failure' ? 'error' : 'default'} variant="outlined" />
                          ) : (
                            <Chip size="small" label={a.status || '-'} color="warning" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {a.htmlUrl ? (
                            <Link href={a.htmlUrl} target="_blank" rel="noopener noreferrer">
                              Open
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MigrationGitHubWorkflowsSection;
